import { FieldErrors, useFormContext } from 'react-hook-form'
import { useCallback, useMemo, useContext, useState } from 'react'
import Button from '../../Button'
import { AuftragOption, AuftragOptionen, BefehlFormField } from '../Generic'
import { DeleteIcon24 } from '../../../icons'
import { useTranslation } from 'react-i18next'
import { BetriebsstelleField, BetriebsstellenartField, Input } from '../../form'
import { cn } from '../../../utils'
import SlideToUnlock from '../../SlideToUnlock'
import MarkAuftraegeAsDoneContext from '../../../contexts/MarkAuftraegeAsDoneContext'

export interface Auftrag1_10ZeileFormProps {
  zeile: number
  auftragKennung: string
  fieldPrefix: string
  readOnly: boolean
  testId: string
  showRemoveButton: boolean
  displaySlideToUnlock: boolean
  onRemove?: () => void
  disabled?: boolean
  activateEmptyFieldWarnings: boolean
  errors?: FieldErrors
}

function Auftrag1_10ZeileForm({
  zeile,
  fieldPrefix,
  readOnly,
  testId,
  showRemoveButton,
  displaySlideToUnlock,
  onRemove,
  disabled = false,
  activateEmptyFieldWarnings,
  errors
}: Readonly<Auftrag1_10ZeileFormProps>) {
  const { t } = useTranslation('ui-befehle')

  const formContext = useFormContext()
  const { control, setValue, formState } = formContext
  const isFirstRow = zeile === 1

  const fieldErrors = useMemo(() => {
    return errors ?? formState.errors
  }, [errors, formState.errors])

  const fieldPaths = useMemo(
    () => ({
      typePath: `${fieldPrefix}.type`,
      kmPath: `${fieldPrefix}.km`,
      signalPath: `${fieldPrefix}.signal`,
      betriebsstellenartPath: `${fieldPrefix}.zugmeldestelle.art`,
      zugmeldestellePath: `${fieldPrefix}.zugmeldestelle.name`
    }),
    [fieldPrefix]
  )

  const [watchedType, watchedBetriebsstellenart] = formContext.watch([
    fieldPaths.typePath,
    fieldPaths.betriebsstellenartPath
  ])

  const renderRemoveButton = useCallback(() => {
    if (!showRemoveButton || disabled) return null
    return (
      <Button
        type="button"
        variant="tertiary-plain"
        onClick={onRemove}
        className="text-gray-400 hover:text-red-600 flex items-center min-h-[44px] min-w-[44px] px-1 py-0.5 -mr-1 -mt-0.5 -mb-0.5"
        data-testid={`${testId}-remove-button`}
        disabled={disabled}
      >
        <DeleteIcon24 data-testid={`${testId}-remove-button-icon`} />
        <span
          className="ml-0.5 text-sm"
          data-testid={`${testId}-remove-button-text`}
        >
          {t('auftraege.1_10.zeile.remove')}
        </span>
      </Button>
    )
  }, [showRemoveButton, disabled, onRemove, t, testId])

  const translationKeys = useMemo(
    () => ({
      km: `auftraege.1_10.zeile.1_1${zeile}_1`,
      signal: `auftraege.1_10.zeile.1_1${zeile}_2`,
      kmInputLabel: 'auftraege.1_10.zeile.km_input_label',
      signalInputLabel: 'auftraege.1_10.zeile.signal_input_label'
    }),
    [zeile]
  )

  const clearConflictingFields = useCallback(
    (newValue: string) => {
      if (newValue === 'km') {
        setValue(fieldPaths.signalPath, '', {
          shouldValidate: false,
          shouldDirty: false
        })
        setValue(fieldPaths.betriebsstellenartPath, '', {
          shouldValidate: false,
          shouldDirty: false
        })
        setValue(fieldPaths.zugmeldestellePath, '', {
          shouldValidate: false,
          shouldDirty: false
        })
      } else if (newValue === 'signal_zugmeldestelle') {
        setValue(fieldPaths.kmPath, '', {
          shouldValidate: false,
          shouldDirty: false
        })
      }
    },
    [setValue, fieldPaths]
  )

  const onOptionChange = useCallback(
    (newValue: string, fieldOnChange: (value: string) => void) => {
      fieldOnChange(newValue)
      clearConflictingFields(newValue)
    },
    [clearConflictingFields]
  )

  const context = useContext(MarkAuftraegeAsDoneContext)
  const befehlFrontendId = context?.befehlFrontendId
  const isQuittiert = context?.isBefehlsnachrichtQuittiert

  const [confirmed, setConfirmed] = useState<boolean>(
    context?.isDone(befehlFrontendId + '-1_1' + zeile) || false
  )

  const updateSlideToUnlockState = (value: boolean) => {
    context?.setMarkedAsDone(befehlFrontendId + '-1_1' + zeile, value)
    setConfirmed(value)
  }

  return (
    <div className="flex flex-col gap-0.5 relative -ml-3 sm:ml-0">
      {confirmed && isQuittiert && (
        <div
          className="absolute z-30 inset-0 bg-white/50 dark:bg-cool-gray-800/65 pointer-events-none"
          data-testid={`${testId}-gray-out-overlay`}
        />
      )}
      <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5">
        <div className="sm:w-2 sm:text-right flex justify-start sm:justify-end items-baseline">
          <div className="min-w-[2ch]">
            {isFirstRow ? '\u00A0' : t('auftraege.1_10.zeile.und_label')}
          </div>
        </div>

        <div className={cn('flex flex-col flex-1')}>
          <BefehlFormField
            name={fieldPaths.typePath}
            control={control}
            label="Auftrag Option"
            readOnly={false}
            disabled={disabled}
            activateEmptyWarning={activateEmptyFieldWarnings}
            data-testid={`${testId}-selector`}
            render={({ field }) => (
              <AuftragOptionen
                name={fieldPaths.typePath}
                className={cn('relative', { 'pt-3': showRemoveButton })}
                header={
                  <>
                    {showRemoveButton && (
                      <div className="absolute top-0.5 right-1 z-10">
                        {renderRemoveButton()}
                      </div>
                    )}
                  </>
                }
                slideToUnlock={
                  <>
                    {context && displaySlideToUnlock && (
                      <SlideToUnlock
                        className="pt-0.75"
                        data-testid={`${testId}-abgearbeitet-markieren-slideToUnlock`}
                        confirmed={confirmed}
                        onConfirmedChange={updateSlideToUnlockState}
                        variant="secondary-solid"
                      />
                    )}
                  </>
                }
                value={field.value}
                onChange={(newValue) =>
                  onOptionChange(newValue, field.onChange)
                }
                readOnly={readOnly}
                errors={fieldErrors}
                data-testid={`${testId}-selector-options`}
              >
                <AuftragOption
                  value="km"
                  label={t(translationKeys.km)}
                  data-testid={`${testId}-selector-options-km`}
                >
                  <div className="flex w-full">
                    <div className="w-full">
                      <BefehlFormField
                        name={fieldPaths.kmPath}
                        control={control}
                        label={t(translationKeys.kmInputLabel)}
                        readOnly={readOnly}
                        disabled={disabled || watchedType !== 'km'}
                        data-testid={`${testId}-feld-1-1${zeile}-1`}
                        variant="solid"
                        render={({
                          field: kmField,
                          isDisabled: isKmDisabled
                        }) => (
                          <Input
                            {...kmField}
                            name={fieldPaths.kmPath}
                            label={t(translationKeys.kmInputLabel)}
                            errors={fieldErrors}
                            className="w-full"
                            data-testid={`${testId}-feld-1-1${zeile}-1-inner`}
                            disabled={isKmDisabled}
                          />
                        )}
                      />
                    </div>
                  </div>
                </AuftragOption>
                <AuftragOption
                  value="signal_zugmeldestelle"
                  label={t(translationKeys.signal)}
                  data-testid={`${testId}-zeile${zeile}-signal-option`}
                >
                  <div className="@container">
                    <div className="flex flex-col @[400px]:flex-row w-full gap-0.5">
                      <div className="flex-1">
                        <BefehlFormField
                          name={fieldPaths.signalPath}
                          control={control}
                          label={t(translationKeys.signalInputLabel)}
                          readOnly={readOnly}
                          disabled={
                            disabled || watchedType !== 'signal_zugmeldestelle'
                          }
                          data-testid={`${testId}-zeile${zeile}-signal-input`}
                          variant="solid"
                          render={({
                            field: signalField,
                            isDisabled: isSignalDisabled
                          }) => (
                            <Input
                              {...signalField}
                              label={t(translationKeys.signalInputLabel)}
                              errors={fieldErrors}
                              className="w-full"
                              data-testid={`${testId}-zeile${zeile}-signal-input-inner`}
                              disabled={isSignalDisabled}
                            />
                          )}
                        />
                      </div>
                      <div className="flex-1">
                        <BefehlFormField
                          name={fieldPaths.betriebsstellenartPath}
                          control={control}
                          label={t(
                            'auftraege.1_10.zeile.betriebsstellenart_label'
                          )}
                          readOnly={readOnly}
                          disabled={
                            disabled || watchedType !== 'signal_zugmeldestelle'
                          }
                          useTextDisplayInReadOnly={true}
                          data-testid={`${testId}-zeile${zeile}-betriebsstellenart-select`}
                          variant="solid"
                          render={({
                            field: bstTypField,
                            isDisabled: isBstTypDisabled,
                            isFieldEmpty
                          }) => (
                            <BetriebsstellenartField
                              {...bstTypField}
                              label={t(
                                'auftraege.1_10.zeile.betriebsstellenart_label'
                              )}
                              variant="solid"
                              errors={fieldErrors}
                              className="w-full"
                              activateEmptyWarning={
                                activateEmptyFieldWarnings &&
                                isFieldEmpty &&
                                !isBstTypDisabled
                              }
                              data-testid={`${testId}-zeile${zeile}-betriebsstellenart-select-inner`}
                              disabled={isBstTypDisabled}
                              selectClassName="w-full"
                            />
                          )}
                        />
                      </div>
                      <div className="flex-1">
                        <BefehlFormField
                          name={fieldPaths.zugmeldestellePath}
                          control={control}
                          label={t('auftraege.1_10.zeile.zugmeldestelle_label')}
                          readOnly={readOnly}
                          disabled={
                            disabled || watchedType !== 'signal_zugmeldestelle'
                          }
                          useTextDisplayInReadOnly={true}
                          data-testid={`${testId}-zeile${zeile}-zugmeldestelle-input`}
                          variant="solid"
                          render={({
                            field: zmField,
                            isDisabled: isZmDisabled,
                            isFieldEmpty
                          }) => (
                            <BetriebsstelleField
                              {...zmField}
                              label={t(
                                'auftraege.1_10.zeile.zugmeldestelle_label'
                              )}
                              errors={fieldErrors}
                              className="w-full"
                              activateEmptyWarning={
                                activateEmptyFieldWarnings &&
                                isFieldEmpty &&
                                !isZmDisabled
                              }
                              data-testid={`${testId}-zeile${zeile}-zugmeldestelle-input-inner`}
                              disabled={isZmDisabled}
                              typ={watchedBetriebsstellenart}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </AuftragOption>
              </AuftragOptionen>
            )}
          />
        </div>
      </div>
    </div>
  )
}

export default Auftrag1_10ZeileForm
